// The news grid: Fibonacci-lattice points on the sphere, in place of H3 cells.
//
// Point i of N sits at z = 1 - 2(i + 1/2)/N and longitude i × golden angle,
// so points are almost evenly spaced everywhere (no pentagons, no cells that
// are 1.9x larger than their neighbours) and index order is latitude order:
// every latitude band is one contiguous run of indices, which makes "nearest
// point" and "points in a region" cheap without any index structure.
//
// Grid levels mirror the H3 resolutions they replace (3 from orbit, then 4
// and 5 as the camera comes down). A cell id is a number:
// res * ID_STRIDE + point index.

export const BASE_RES = 3;
const ID_STRIDE = 1e7; // above the largest level's point count
// How many times denser than the original grid (H3's point counts per level:
// ~41k points from orbit) this one is. Point spacing, dots and circles shrink
// by sqrt(GRID_DENSITY); smoothing widths, hover distances and column heights
// stay the same size on the globe. Level 5 must stay under ID_STRIDE points
// (2.0M × GRID_DENSITY × NET_FACTOR), so the product can be at most ~4.9.
//
// Two lattices per level share one id space. Articles land on the coarser
// "article" lattice (GRID_DENSITY): those are the only points that can be
// filled with a circle. The visible net of dots is NET_FACTOR times denser,
// and every article point is also a net point (its nearest one).
export const GRID_DENSITY = 2;
export const NET_FACTOR = 2;
const ORIGINAL_POINT_COUNTS = { 3: 41162, 4: 288122, 5: 2016842 };
const scaledCounts = (factor) =>
  Object.fromEntries(Object.entries(ORIGINAL_POINT_COUNTS).map(([res, n]) => [res, Math.round(n * factor)]));
const ARTICLE_COUNTS = scaledCounts(GRID_DENSITY);
const POINT_COUNTS = scaledCounts(GRID_DENSITY * NET_FACTOR);
export const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const DEG = Math.PI / 180;

export const pointCount = (res) => POINT_COUNTS[res];
export const cellRes = (id) => Math.floor(id / ID_STRIDE);
export const cellIndex = (id) => id - cellRes(id) * ID_STRIDE;
export const makeCellId = (res, index) => res * ID_STRIDE + index;

// Average angular distance between neighbouring article points (radians): the
// side of the square each owns on the unit sphere. Circle sizes and the
// per-level scale follow this; the finer net spacing is netSpacingRad.
export const spacingRad = (res) => Math.sqrt((4 * Math.PI) / ARTICLE_COUNTS[res]);
export const netSpacingRad = (res) => Math.sqrt((4 * Math.PI) / POINT_COUNTS[res]);

// The "ring" length the smoothing widths and kernels were tuned in: the
// distance between adjacent H3 cell centres, which was 1.084 × the original
// grid's spacing. It doesn't change with GRID_DENSITY, so peaks keep their
// width on the globe as the grid gets finer.
export const ringUnitRad = (res) => Math.sqrt((4 * Math.PI) / ORIGINAL_POINT_COUNTS[res]) * 1.084;

// How much smaller than the original a dot's pixel size should be (dots are
// on the net, so this follows its density).
export const dotSizeScale = 1 / Math.sqrt(GRID_DENSITY * NET_FACTOR);

// How big a level's dots are relative to the base level (sizes, distances and
// heights tuned for the base level scale by this).
export const cellScale = (res) => spacingRad(res) / spacingRad(BASE_RES);

// Angular radius of a filled circle at a level, in point spacings. Independent
// of the grid's resolution. 0.457 just fit between neighbours (0.85–1.1
// spacings apart); 0.6 is deliberately bigger, so adjacent occupied points
// overlap a little and read as a blob. Lower it to ~0.42 to rule overlap out.
export const CIRCLE_RADIUS_SPACINGS = 0.6;
export const circleRadiusRad = (res) => spacingRad(res) * CIRCLE_RADIUS_SPACINGS;

// Position of point `index` at a level: [lat, lng] in degrees.
export function pointLatLng(res, index) {
  return latLngOfPoint(POINT_COUNTS[res], index);
}

function latLngOfPoint(n, index) {
  const lat = Math.asin(1 - (2 * (index + 0.5)) / n) / DEG;
  let lng = ((index * GOLDEN_ANGLE) / DEG) % 360;
  if (lng >= 180) lng -= 360;
  return [lat, lng];
}

export function cellToLatLng(id) {
  return pointLatLng(cellRes(id), cellIndex(id));
}

// Indices whose z lies in [zLo, zHi] (a latitude band), as [first, last].
export function bandIndices(res, zLo, zHi) {
  const n = POINT_COUNTS[res];
  const first = Math.max(0, Math.ceil(((1 - zHi) * n) / 2 - 0.5));
  const last = Math.min(n - 1, Math.floor(((1 - zLo) * n) / 2 - 0.5));
  return [first, last];
}

// The nearest point to (lat, lng) at a level, by spherical Fibonacci
// inversion (Keinert et al., "Spherical Fibonacci Mapping"): the lattice is
// locally a skewed square grid, spanned by the offsets of two consecutive
// Fibonacci numbers of indices. Solving for the target's coordinates in that
// basis names a handful of candidate points, so a lookup costs ~16 distance
// checks however many points there are.
const PHI = (1 + Math.sqrt(5)) / 2;
const SQRT5 = Math.sqrt(5);
const TWO_PI = 2 * Math.PI;
const LNG_TURN = 2 - PHI; // GOLDEN_ANGLE / 2π
const LOG_PHI2 = Math.log(PHI * PHI);
const wrapPi = (a) => a - TWO_PI * Math.round(a / TWO_PI);
const frac = (x) => x - Math.floor(x);

const bases = new Map(); // `${n}:${k}` → basis at Fibonacci scale k
function basisFor(n, k) {
  const key = n * 64 + k;
  let b = bases.get(key);
  if (b) return b;
  const fk = Math.pow(PHI, k) / SQRT5;
  const f0 = Math.round(fk);
  const f1 = Math.round(fk * PHI);
  // Longitude (wrapped) and z steps of moving f0 or f1 indices along.
  const m00 = wrapPi(TWO_PI * frac(f0 * LNG_TURN));
  const m01 = wrapPi(TWO_PI * frac(f1 * LNG_TURN));
  const m10 = (-2 * f0) / n;
  const m11 = (-2 * f1) / n;
  const det = m00 * m11 - m01 * m10;
  b = { f0, f1, i00: m11 / det, i01: -m01 / det, i10: -m10 / det, i11: m00 / det };
  bases.set(key, b);
  return b;
}

// The nearest net point at a level.
export function latLngToNetCell(lat, lng, res) {
  return makeCellId(res, nearestIndex(lat, lng, POINT_COUNTS[res]));
}

// The cell an article at (lat, lng) belongs to: the net point at the nearest
// article-lattice point, so the same place always lands on the same dot and
// only every NET_FACTOR-th net dot can hold news.
export function latLngToCell(lat, lng, res) {
  const [aLat, aLng] = latLngOfPoint(ARTICLE_COUNTS[res], nearestIndex(lat, lng, ARTICLE_COUNTS[res]));
  return latLngToNetCell(aLat, aLng, res);
}

function nearestIndex(lat, lng, n) {
  const z = Math.sin(lat * DEG);
  const oneMinusZ2 = Math.max(0, 1 - z * z);
  const sideXY = Math.sqrt(oneMinusZ2);
  const lngRad = lng * DEG;
  const k = Math.max(2, Math.floor(Math.log(n * Math.PI * SQRT5 * oneMinusZ2) / LOG_PHI2));
  const { f0, f1, i00, i01, i10, i11 } = basisFor(n, k);
  // Index 0 sits at longitude 0 and z = 1 - 1/n.
  const rhsLng = wrapPi(lngRad);
  const rhsZ = z - (1 - 1 / n);
  const af = Math.floor(i00 * rhsLng + i01 * rhsZ);
  const bf = Math.floor(i10 * rhsLng + i11 * rhsZ);
  let best = 0;
  let bestDot = -2;
  for (let da = -1; da <= 2; da++) {
    for (let db = -1; db <= 2; db++) {
      let i = (af + da) * f0 + (bf + db) * f1;
      // Past a pole, fold back onto the sphere.
      if (i < 0) i = -1 - i;
      else if (i >= n) i = 2 * n - 1 - i;
      if (i < 0 || i >= n) continue;
      const zi = 1 - (2 * i + 1) / n;
      const dot = z * zi + sideXY * Math.sqrt(Math.max(0, 1 - zi * zi)) * Math.cos(lngRad - i * GOLDEN_ANGLE);
      if (dot > bestDot) {
        bestDot = dot;
        best = i;
      }
    }
  }
  return best;
}

// Net points within `radiusRad` of a cell's centre (the dots a filled circle
// covers), as ids.
export function netCellsWithin(id, radiusRad) {
  const res = cellRes(id);
  const [lat, lng] = cellToLatLng(id);
  const latR = lat * DEG;
  const zc = Math.sin(latR);
  const zLo = latR - radiusRad <= -Math.PI / 2 ? -1 : Math.sin(latR - radiusRad);
  const zHi = latR + radiusRad >= Math.PI / 2 ? 1 : Math.sin(latR + radiusRad);
  const [first, last] = bandIndices(res, zLo, zHi);
  const n = POINT_COUNTS[res];
  const cosR = Math.cos(radiusRad);
  const xy = Math.cos(latR);
  const lngR = lng * DEG;
  const out = [];
  for (let i = first; i <= last; i++) {
    const zi = 1 - (2 * i + 1) / n;
    const dot = zi * zc + Math.sqrt(Math.max(0, 1 - zi * zi)) * xy * Math.cos(i * GOLDEN_ANGLE - lngR);
    if (dot >= cosR) out.push(makeCellId(res, i));
  }
  return out;
}

// Every point at a level, as ids.
export function allCells(res) {
  const n = POINT_COUNTS[res];
  const cells = new Array(n);
  for (let i = 0; i < n; i++) cells[i] = makeCellId(res, i);
  return cells;
}

// The point at angular distance `radiusRad` from a point's centre along a
// bearing (radians clockwise from north): used to anchor callouts on a
// dot's edge. Returns [lat, lng] in degrees.
export function offsetLatLng(lat, lng, radiusRad, bearing) {
  const phi = lat * DEG;
  const sinPhi2 = Math.sin(phi) * Math.cos(radiusRad) + Math.cos(phi) * Math.sin(radiusRad) * Math.cos(bearing);
  const phi2 = Math.asin(Math.max(-1, Math.min(1, sinPhi2)));
  const lam2 =
    lng * DEG +
    Math.atan2(
      Math.sin(bearing) * Math.sin(radiusRad) * Math.cos(phi),
      Math.cos(radiusRad) - Math.sin(phi) * Math.sin(phi2)
    );
  return [phi2 / DEG, ((((lam2 / DEG + 180) % 360) + 360) % 360) - 180];
}
